/**
 * Fantasy Console 0 - Sprite Utilities
 *
 * Core algorithms for sprite manipulation, drawing operations,
 * and import/export functionality.
 */

import {
  SPRITE_SIZE,
  SPRITE_SHEET_SIZE,
  SPRITES_PER_ROW,
  ColorIndex,
} from '@fc0/types'

/**
 * PICO-8 color palette RGB values
 */
export const PICO8_PALETTE = [
  '#000000',
  '#1d2b53',
  '#7e2553',
  '#008751',
  '#ab5236',
  '#5f574f',
  '#c2c3c7',
  '#fff1e8',
  '#ff004d',
  '#ffa300',
  '#ffec27',
  '#00e436',
  '#29adff',
  '#83769c',
  '#ff77a8',
  '#ffccaa',
] as const

/**
 * PICO-8 palette as RGB arrays for color matching
 */
export const PICO8_RGB: [number, number, number][] = [
  [0, 0, 0],
  [29, 43, 83],
  [126, 37, 83],
  [0, 135, 81],
  [171, 82, 54],
  [95, 87, 79],
  [194, 195, 199],
  [255, 241, 232],
  [255, 0, 77],
  [255, 163, 0],
  [255, 236, 39],
  [0, 228, 54],
  [41, 173, 255],
  [131, 118, 156],
  [255, 119, 168],
  [255, 204, 170],
]

/**
 * Get the byte offset in the sprite sheet for a given sprite index
 * Sprites are arranged in a 16x16 grid (256 sprites total)
 */
export function getSpriteOffset(spriteIndex: number): number {
  const col = spriteIndex % SPRITES_PER_ROW
  const row = Math.floor(spriteIndex / SPRITES_PER_ROW)
  return row * SPRITE_SIZE * SPRITE_SHEET_SIZE + col * SPRITE_SIZE
}

/**
 * Get a pixel from the sprite sheet at (x, y) coordinates
 */
export function getPixel(
  sprites: Uint8Array,
  x: number,
  y: number,
): ColorIndex {
  if (x < 0 || x >= SPRITE_SHEET_SIZE || y < 0 || y >= SPRITE_SHEET_SIZE) {
    return 0 as ColorIndex
  }
  return sprites[y * SPRITE_SHEET_SIZE + x] as ColorIndex
}

/**
 * Set a pixel in the sprite sheet at (x, y) coordinates
 * Returns a new Uint8Array (immutable operation)
 */
export function setPixel(
  sprites: Uint8Array,
  x: number,
  y: number,
  color: ColorIndex,
): Uint8Array {
  if (x < 0 || x >= SPRITE_SHEET_SIZE || y < 0 || y >= SPRITE_SHEET_SIZE) {
    return sprites
  }
  const newSprites = new Uint8Array(sprites)
  newSprites[y * SPRITE_SHEET_SIZE + x] = color
  return newSprites
}

/**
 * Set a pixel in the sprite sheet (mutating operation for batch updates)
 */
export function setPixelMut(
  sprites: Uint8Array,
  x: number,
  y: number,
  color: ColorIndex,
): void {
  if (x < 0 || x >= SPRITE_SHEET_SIZE || y < 0 || y >= SPRITE_SHEET_SIZE) {
    return
  }
  sprites[y * SPRITE_SHEET_SIZE + x] = color
}

/**
 * Extract an 8x8 sprite from the sprite sheet
 */
export function extractSprite(
  sprites: Uint8Array,
  spriteIndex: number,
): Uint8Array {
  const result = new Uint8Array(SPRITE_SIZE * SPRITE_SIZE)
  const col = spriteIndex % SPRITES_PER_ROW
  const row = Math.floor(spriteIndex / SPRITES_PER_ROW)
  const baseX = col * SPRITE_SIZE
  const baseY = row * SPRITE_SIZE

  for (let y = 0; y < SPRITE_SIZE; y++) {
    for (let x = 0; x < SPRITE_SIZE; x++) {
      result[y * SPRITE_SIZE + x] = getPixel(sprites, baseX + x, baseY + y)
    }
  }
  return result
}

/**
 * Insert an 8x8 sprite into the sprite sheet
 * Returns a new Uint8Array (immutable operation)
 */
export function insertSprite(
  sprites: Uint8Array,
  spriteIndex: number,
  spriteData: Uint8Array,
): Uint8Array {
  const newSprites = new Uint8Array(sprites)
  const col = spriteIndex % SPRITES_PER_ROW
  const row = Math.floor(spriteIndex / SPRITES_PER_ROW)
  const baseX = col * SPRITE_SIZE
  const baseY = row * SPRITE_SIZE

  for (let y = 0; y < SPRITE_SIZE; y++) {
    for (let x = 0; x < SPRITE_SIZE; x++) {
      const srcIndex = y * SPRITE_SIZE + x
      const dstIndex = (baseY + y) * SPRITE_SHEET_SIZE + (baseX + x)
      newSprites[dstIndex] = spriteData[srcIndex]
    }
  }
  return newSprites
}

/**
 * Clear a sprite (set all pixels to color 0)
 */
export function clearSprite(
  sprites: Uint8Array,
  spriteIndex: number,
): Uint8Array {
  const emptySprite = new Uint8Array(SPRITE_SIZE * SPRITE_SIZE)
  return insertSprite(sprites, spriteIndex, emptySprite)
}

/**
 * Flood fill algorithm using iterative approach (prevents stack overflow)
 */
export function floodFill(
  sprites: Uint8Array,
  startX: number,
  startY: number,
  newColor: ColorIndex,
  bounds?: { minX: number; minY: number; maxX: number; maxY: number },
): Uint8Array {
  const minX = bounds?.minX ?? 0
  const minY = bounds?.minY ?? 0
  const maxX = bounds?.maxX ?? SPRITE_SHEET_SIZE - 1
  const maxY = bounds?.maxY ?? SPRITE_SHEET_SIZE - 1

  if (startX < minX || startX > maxX || startY < minY || startY > maxY) {
    return sprites
  }

  const targetColor = getPixel(sprites, startX, startY)
  if (targetColor === newColor) {
    return sprites
  }

  const newSprites = new Uint8Array(sprites)
  const stack: [number, number][] = [[startX, startY]]
  const visited = new Set<string>()

  while (stack.length > 0) {
    const [x, y] = stack.pop()!
    const key = `${x},${y}`

    if (visited.has(key)) continue
    if (x < minX || x > maxX || y < minY || y > maxY) continue
    if (newSprites[y * SPRITE_SHEET_SIZE + x] !== targetColor) continue

    visited.add(key)
    newSprites[y * SPRITE_SHEET_SIZE + x] = newColor

    stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1])
  }

  return newSprites
}

/**
 * Bresenham's line algorithm
 * Returns array of points along the line
 */
export function getLinePoints(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): [number, number][] {
  const points: [number, number][] = []
  const dx = Math.abs(x1 - x0)
  const dy = Math.abs(y1 - y0)
  const sx = x0 < x1 ? 1 : -1
  const sy = y0 < y1 ? 1 : -1
  let err = dx - dy

  let x = x0
  let y = y0

  while (true) {
    points.push([x, y])
    if (x === x1 && y === y1) break

    const e2 = 2 * err
    if (e2 > -dy) {
      err -= dy
      x += sx
    }
    if (e2 < dx) {
      err += dx
      y += sy
    }
  }

  return points
}

/**
 * Draw a line on the sprite sheet
 */
export function drawLine(
  sprites: Uint8Array,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  color: ColorIndex,
): Uint8Array {
  const newSprites = new Uint8Array(sprites)
  const points = getLinePoints(x0, y0, x1, y1)

  for (const [x, y] of points) {
    setPixelMut(newSprites, x, y, color)
  }

  return newSprites
}

/**
 * Get points for a rectangle (outline or filled)
 */
export function getRectPoints(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  filled: boolean,
): [number, number][] {
  const points: [number, number][] = []
  const minX = Math.min(x0, x1)
  const maxX = Math.max(x0, x1)
  const minY = Math.min(y0, y1)
  const maxY = Math.max(y0, y1)

  if (filled) {
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        points.push([x, y])
      }
    }
  } else {
    for (let x = minX; x <= maxX; x++) {
      points.push([x, minY])
      points.push([x, maxY])
    }
    for (let y = minY + 1; y < maxY; y++) {
      points.push([minX, y])
      points.push([maxX, y])
    }
  }

  return points
}

/**
 * Draw a rectangle on the sprite sheet
 */
export function drawRect(
  sprites: Uint8Array,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  color: ColorIndex,
  filled: boolean,
): Uint8Array {
  const newSprites = new Uint8Array(sprites)
  const points = getRectPoints(x0, y0, x1, y1, filled)

  for (const [x, y] of points) {
    setPixelMut(newSprites, x, y, color)
  }

  return newSprites
}

/**
 * Flip a sprite horizontally
 */
export function flipSpriteH(spriteData: Uint8Array): Uint8Array {
  const result = new Uint8Array(SPRITE_SIZE * SPRITE_SIZE)

  for (let y = 0; y < SPRITE_SIZE; y++) {
    for (let x = 0; x < SPRITE_SIZE; x++) {
      result[y * SPRITE_SIZE + x] =
        spriteData[y * SPRITE_SIZE + (SPRITE_SIZE - 1 - x)]
    }
  }

  return result
}

/**
 * Flip a sprite vertically
 */
export function flipSpriteV(spriteData: Uint8Array): Uint8Array {
  const result = new Uint8Array(SPRITE_SIZE * SPRITE_SIZE)

  for (let y = 0; y < SPRITE_SIZE; y++) {
    for (let x = 0; x < SPRITE_SIZE; x++) {
      result[y * SPRITE_SIZE + x] =
        spriteData[(SPRITE_SIZE - 1 - y) * SPRITE_SIZE + x]
    }
  }

  return result
}

/**
 * Rotate a sprite 90 degrees clockwise
 */
export function rotateSpriteC90(spriteData: Uint8Array): Uint8Array {
  const result = new Uint8Array(SPRITE_SIZE * SPRITE_SIZE)

  for (let y = 0; y < SPRITE_SIZE; y++) {
    for (let x = 0; x < SPRITE_SIZE; x++) {
      result[x * SPRITE_SIZE + (SPRITE_SIZE - 1 - y)] =
        spriteData[y * SPRITE_SIZE + x]
    }
  }

  return result
}

/**
 * Rotate a sprite 90 degrees counter-clockwise
 */
export function rotateSpriteCC90(spriteData: Uint8Array): Uint8Array {
  const result = new Uint8Array(SPRITE_SIZE * SPRITE_SIZE)

  for (let y = 0; y < SPRITE_SIZE; y++) {
    for (let x = 0; x < SPRITE_SIZE; x++) {
      result[(SPRITE_SIZE - 1 - x) * SPRITE_SIZE + y] =
        spriteData[y * SPRITE_SIZE + x]
    }
  }

  return result
}

/**
 * Shift sprite pixels in a direction with wrapping
 */
export function shiftSprite(
  spriteData: Uint8Array,
  direction: 'up' | 'down' | 'left' | 'right',
): Uint8Array {
  const result = new Uint8Array(SPRITE_SIZE * SPRITE_SIZE)

  for (let y = 0; y < SPRITE_SIZE; y++) {
    for (let x = 0; x < SPRITE_SIZE; x++) {
      let srcX = x
      let srcY = y

      switch (direction) {
        case 'up':
          srcY = (y + 1) % SPRITE_SIZE
          break
        case 'down':
          srcY = (y - 1 + SPRITE_SIZE) % SPRITE_SIZE
          break
        case 'left':
          srcX = (x + 1) % SPRITE_SIZE
          break
        case 'right':
          srcX = (x - 1 + SPRITE_SIZE) % SPRITE_SIZE
          break
      }

      result[y * SPRITE_SIZE + x] = spriteData[srcY * SPRITE_SIZE + srcX]
    }
  }

  return result
}

/**
 * Calculate color distance (simple RGB distance)
 */
function colorDistance(
  r1: number,
  g1: number,
  b1: number,
  r2: number,
  g2: number,
  b2: number,
): number {
  return Math.sqrt(
    Math.pow(r1 - r2, 2) + Math.pow(g1 - g2, 2) + Math.pow(b1 - b2, 2),
  )
}

/**
 * Find the nearest PICO-8 color for an RGB value
 */
export function nearestPico8Color(r: number, g: number, b: number): ColorIndex {
  let minDistance = Infinity
  let nearest: ColorIndex = 0

  for (let i = 0; i < PICO8_RGB.length; i++) {
    const [pr, pg, pb] = PICO8_RGB[i]
    const distance = colorDistance(r, g, b, pr, pg, pb)
    if (distance < minDistance) {
      minDistance = distance
      nearest = i as ColorIndex
    }
  }

  return nearest
}

/**
 * Render a sprite sheet to a canvas
 */
export function renderSpriteSheetToCanvas(
  sprites: Uint8Array,
  canvas: HTMLCanvasElement,
  scale: number = 1,
): void {
  canvas.width = SPRITE_SHEET_SIZE * scale
  canvas.height = SPRITE_SHEET_SIZE * scale
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  const imageData = ctx.createImageData(SPRITE_SHEET_SIZE, SPRITE_SHEET_SIZE)

  for (let i = 0; i < sprites.length; i++) {
    const colorIndex = sprites[i]
    const [r, g, b] = PICO8_RGB[colorIndex]
    const pixelIndex = i * 4
    imageData.data[pixelIndex] = r
    imageData.data[pixelIndex + 1] = g
    imageData.data[pixelIndex + 2] = b
    imageData.data[pixelIndex + 3] = 255
  }

  if (scale === 1) {
    ctx.putImageData(imageData, 0, 0)
  } else {
    const tempCanvas = document.createElement('canvas')
    tempCanvas.width = SPRITE_SHEET_SIZE
    tempCanvas.height = SPRITE_SHEET_SIZE
    const tempCtx = tempCanvas.getContext('2d')
    if (tempCtx) {
      tempCtx.putImageData(imageData, 0, 0)
      ctx.imageSmoothingEnabled = false
      ctx.drawImage(tempCanvas, 0, 0, canvas.width, canvas.height)
    }
  }
}

/**
 * Export sprite sheet as PNG
 */
export function exportSpriteSheetPNG(sprites: Uint8Array): void {
  const canvas = document.createElement('canvas')
  renderSpriteSheetToCanvas(sprites, canvas, 1)

  canvas.toBlob((blob) => {
    if (!blob) return

    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'sprites.png'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, 'image/png')
}

/**
 * Import sprite sheet from PNG file
 * Returns a promise with the new sprite data
 */
export function importSpriteSheetPNG(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      const img = new Image()

      img.onload = () => {
        if (
          img.width !== SPRITE_SHEET_SIZE ||
          img.height !== SPRITE_SHEET_SIZE
        ) {
          reject(
            new Error(
              `Image must be ${SPRITE_SHEET_SIZE}x${SPRITE_SHEET_SIZE} pixels`,
            ),
          )
          return
        }

        const canvas = document.createElement('canvas')
        canvas.width = SPRITE_SHEET_SIZE
        canvas.height = SPRITE_SHEET_SIZE
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          reject(new Error('Could not get canvas context'))
          return
        }

        ctx.drawImage(img, 0, 0)
        const imageData = ctx.getImageData(
          0,
          0,
          SPRITE_SHEET_SIZE,
          SPRITE_SHEET_SIZE,
        )
        const sprites = new Uint8Array(SPRITE_SHEET_SIZE * SPRITE_SHEET_SIZE)

        for (let i = 0; i < sprites.length; i++) {
          const pixelIndex = i * 4
          const r = imageData.data[pixelIndex]
          const g = imageData.data[pixelIndex + 1]
          const b = imageData.data[pixelIndex + 2]
          sprites[i] = nearestPico8Color(r, g, b)
        }

        resolve(sprites)
      }

      img.onerror = () => {
        reject(new Error('Failed to load image'))
      }

      img.src = e.target?.result as string
    }

    reader.onerror = () => {
      reject(new Error('Failed to read file'))
    }

    reader.readAsDataURL(file)
  })
}

/**
 * Get sprite coordinates from sprite index
 */
export function getSpriteCoords(spriteIndex: number): { x: number; y: number } {
  const col = spriteIndex % SPRITES_PER_ROW
  const row = Math.floor(spriteIndex / SPRITES_PER_ROW)
  return { x: col * SPRITE_SIZE, y: row * SPRITE_SIZE }
}

/**
 * Get sprite index from click position on sprite sheet
 */
export function getSpriteIndexFromPosition(
  x: number,
  y: number,
  canvasWidth: number,
  canvasHeight: number,
): number {
  const scale = canvasWidth / SPRITE_SHEET_SIZE
  const pixelX = Math.floor(x / scale)
  const pixelY = Math.floor(y / scale)
  const col = Math.floor(pixelX / SPRITE_SIZE)
  const row = Math.floor(pixelY / SPRITE_SIZE)
  return Math.min(255, row * SPRITES_PER_ROW + col)
}

/**
 * Get pixel position within a sprite from editor click position
 */
export function getPixelFromEditorPosition(
  x: number,
  y: number,
  canvasSize: number,
  spriteIndex: number,
): { x: number; y: number; sheetX: number; sheetY: number } | null {
  const pixelSize = canvasSize / SPRITE_SIZE
  const pixelX = Math.floor(x / pixelSize)
  const pixelY = Math.floor(y / pixelSize)

  if (
    pixelX < 0 ||
    pixelX >= SPRITE_SIZE ||
    pixelY < 0 ||
    pixelY >= SPRITE_SIZE
  ) {
    return null
  }

  const spriteCoords = getSpriteCoords(spriteIndex)
  return {
    x: pixelX,
    y: pixelY,
    sheetX: spriteCoords.x + pixelX,
    sheetY: spriteCoords.y + pixelY,
  }
}
